import type { RunState, Tool } from '@wikigold/engine';
import type { Rules, CampaignConfig, ActivityDate } from '@wikigold/rules';
import copy from './i18n/zh-CN.json';
import { createIdempotencyKey } from './idempotency';
export const t = copy;
export type Page = 'S01' | 'S02' | 'S03' | 'S04' | 'S05' | 'S06' | 'S07' | 'S08' | 'S09' | 'S10';
export const pagePaths: Record<Page, string> = { S01: '/', S02: '/tutorial', S03: '/register', S04: '/play', S05: '/result', S06: '/progress', S07: '/leaderboard', S08: '/invite', S09: '/evidence', S10: '/rewards' };
export function getPageForPath(path: string): Page { if (path === '/game-preview') return 'S02'; return (Object.entries(pagePaths).find(([, value]) => value === path)?.[0] as Page) ?? 'S01'; }
export function format(value: string, data: Record<string, string | number>): string { return value.replace(/\{(\w+)\}/g, (_, key: string) => String(data[key] ?? '')); }
export function publicRulesVersion(version: string): string { return version.match(/^v?(\d+(?:\.\d+)*)/)?.[1] ?? version; }
export function money(cents: number): string { return `$${Math.trunc(cents / 100)}.${String(cents % 100).padStart(2, '0')}`; }
export function errorMessage(error: unknown): string { const code = error instanceof Error ? error.message : String(error); return (t.errors as Record<string, string>)[code] ?? (/401|unauthor/i.test(code) ? t.forbidden : /version|conflict/i.test(code) ? t.versionConflict : t.error); }
export interface Run { id: string; dateKey: string; tool: Tool; phase: string; state: RunState; startedAt: string; dayEndAt: string; version: number; beans: number; clockFrozen?: boolean; serverNow?: string; }
export interface Participant { id: string; uid?: string | null; registrationSequence?: number | null; nickname: string; version: number; inviteCode?: string; identityStatus?: string; }
export interface Me { participant: Participant | null; runs: Run[]; h5Days: number; beans: number; appStatus: string; validDays: number | {dateKey: string; skillPoints: number}[] | null; evidenceStatus: string; }
export interface Config { rules: Rules; campaign: CampaignConfig; current: ActivityDate; now: string | number; testMock: boolean; frozen?: boolean; clockFrozen?: boolean; }
export type Data = Record<string, any>;
export async function api<T = Data>(path: string, body?: unknown, method?: string): Promise<T> {
  const locale = typeof document === 'undefined' ? 'en' : document.documentElement.lang || 'en';
  const response = await fetch(path, { method: method ?? (body === undefined ? 'GET' : 'POST'), credentials: 'include', headers: { 'accept-language': locale, ...(body === undefined ? {} : { 'content-type': 'application/json' }) }, ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
  const data = await response.json().catch(() => ({})) as Data;
  if (!response.ok) throw new Error(String(data.error ?? data.code ?? response.status));
  return data as T;
}
export function mutation(version = 0): { idempotencyKey: string; version: number } { return { idempotencyKey: createIdempotencyKey(), version }; }
export async function copyText(text: string): Promise<void> {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Plain HTTP and embedded webviews commonly expose the API but reject it.
    }
  }
  if (typeof document !== 'undefined') {
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.left = '-9999px';
    document.body.append(area);
    area.focus();
    area.select();
    try {
      if (document.execCommand?.('copy')) return;
    } finally {
      area.remove();
    }
  }
  throw new Error('clipboard_unavailable');
}
export function statusText(value: unknown): string { return (t.statusLabels as Record<string, string>)[String(value).toLowerCase()] ?? t.unknown; }

export function boardReviewScope(board: { status: string; N?: number | null; K?: number | null }): string {
  if (!['provisional', 'appeal_processing', 'preliminary'].includes(board.status) || !Number.isSafeInteger(board.N) || !Number.isSafeInteger(board.K) || board.N! < 0 || board.K! < 0) return t.unknown;
  return format(t.reviewedScope, { n: board.N!, k: board.K! });
}
